import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Button,
    Typography,
    Alert,
} from "@material-tailwind/react";
import { Form, Formik } from "formik";
import FormikInput from "components/form/formik-input";
import * as Yup from "yup";
import { useRateLimit, sanitiseInput } from "utils/utils.jsx";
import { toast } from "react-toastify";
import { QRCodeSVG } from "qrcode.react";
import {
    classifyError,
    getApiErrorMessage,
} from "utils/api-error";
import { totpField } from "utils/validation";

const TotpSetupDialog = ({
    open,
    onClose,
    onSetupSuccess,
    onSetupSkip,
    onSetupCancel,
    secretCode,
    secret32,
    userEmail,
    onVerify,
    isLoading = false,
    setupError = "",
}) => {
    const { isBlocked, incrementAttempts, resetAttempts } = useRateLimit();

    const qrCodeUrl =
        userEmail && secret32
            ? `otpauth://totp/Gobby:${encodeURIComponent(userEmail)}?secret=${secret32}&issuer=Gobby`
            : "";

    const initialValues = { totpCode: "" };

    const validationSchema = Yup.object().shape({
        totpCode: totpField.required("Authentication code is required"),
    });

    const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
        if (isBlocked) {
            toast.error("Too many attempts. Please try again later.");
            return;
        }
        try {
            const sanitisedCode = sanitiseInput(values.totpCode?.replace(/\s+/g, ""));
            await onVerify(sanitisedCode, secretCode);
            resetAttempts();
            onSetupSuccess();
        } catch (error) {
            const status = classifyError(error);
            const isAuthFailure = status === 401 || status === 403;
            if (isAuthFailure) {
                incrementAttempts();
            }
            setFieldError(
                "totpCode",
                isAuthFailure
                    ? "Invalid authentication code. Please try again."
                    : getApiErrorMessage(status, "totp"),
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            handler={onClose}
            dismissible={false}
            className="dialog"
        >
            <div className="bg-white rounded-xl">
                <DialogHeader className="dialog__header">
                    Set Up Two-Factor Authentication
                </DialogHeader>
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={validationSchema}
                >
                    {({ isValid, dirty, isSubmitting }) => (
                        <Form>
                            <DialogBody className="dialog__body">
                                <div className="font-body py-4">
                                    {isLoading ? (
                                        <Typography className="text-center py-8">
                                            Loading TOTP setup...
                                        </Typography>
                                    ) : setupError ? (
                                        <Alert color="red" className="mb-4">
                                            {setupError}
                                        </Alert>
                                    ) : (
                                        <>
                                            <Typography className="text-primary mb-4">
                                                1. Open the authenticator app on
                                                your phone
                                            </Typography>
                                            <Typography className="text-primary mb-2">
                                                {qrCodeUrl
                                                    ? "2. Choose the option to either:"
                                                    : "2. Manually enter this key:"}
                                            </Typography>
                                            <div className="flex flex-col items-center mb-4">
                                                {qrCodeUrl && (
                                                    <>
                                                        <Typography className="text-sm text-primary mt-2">
                                                            Scan the QR Code
                                                        </Typography>
                                                        <QRCodeSVG
                                                            value={qrCodeUrl}
                                                            size={200}
                                                        />
                                                    </>
                                                )}
                                                {qrCodeUrl && (
                                                    <Typography className="text-primary my-2">
                                                        or
                                                    </Typography>
                                                )}
                                                <Typography className="text-sm text-primary">
                                                    {qrCodeUrl && "Manually enter this key: "}
                                                    <span className="font-mono">
                                                        {secretCode}
                                                    </span>
                                                </Typography>
                                            </div>
                                            <Typography className="text-primary mb-2">
                                                3. Enter the 6 digit number
                                                generated by your authenticator
                                                app in the box below.
                                            </Typography>
                                            {isBlocked && (
                                                <Alert color="red" className="mt-2">
                                                    Too many failed attempts.
                                                    Please try again later.
                                                </Alert>
                                            )}
                                            <FormikInput
                                                autoComplete="one-time-code"
                                                className="font-display text-lg bg-white pt-4 pb-4"
                                                name="totpCode"
                                                type="text"
                                                color="black"
                                                label="Authentication Code"
                                                required
                                            />
                                        </>
                                    )}
                                </div>
                            </DialogBody>
                            <DialogFooter className="dialog__footer pt-0">
                                <div className="flex gap-2 w-full">
                                    {onSetupCancel && (
                                        <Button
                                            type="button"
                                            className="btn btn--secondary"
                                            variant="outlined"
                                            onClick={onSetupCancel}
                                            disabled={isSubmitting}
                                        >
                                            Cancel
                                        </Button>
                                    )}
                                    {onSetupSkip && (
                                        <Button
                                            type="button"
                                            className="btn btn--secondary"
                                            variant="outlined"
                                            onClick={onSetupSkip}
                                        >
                                            Skip for Now
                                        </Button>
                                    )}
                                    <Button
                                        type="submit"
                                        className="btn btn--primary flex-1"
                                        variant="filled"
                                        disabled={
                                            !(isValid && dirty) ||
                                            isSubmitting ||
                                            isBlocked ||
                                            isLoading ||
                                            !!setupError
                                        }
                                    >
                                        {isSubmitting
                                            ? "Verifying..."
                                            : "Verify and Enable"}
                                    </Button>
                                </div>
                            </DialogFooter>
                        </Form>
                    )}
                </Formik>
            </div>
        </Dialog>
    );
};

export default TotpSetupDialog;
